import { observer } from "mobx-react";
import { Button, Form, Input, Popconfirm, Row, Col, Badge, Radio, Checkbox, Select, Tag, TimePicker, Tooltip } from "antd";
import dayjs from "dayjs";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRotateLeft } from "@fortawesome/free-solid-svg-icons";

import styles from "../../../_common/CrudFormStyles.module.scss";
import { translateKey as t } from "../../../../../locales/translation";
import { CronjobController } from "../../CronjobController";

const { TextArea } = Input;

const TIMEZONES = [
  { value: "UTC", label: "UTC" },
  { value: "Europe/Malta", label: "Europe/Malta" },
];

const DAY_OPTIONS = [
  { label: t("lblMon", "Mon"), value: 1 },
  { label: t("lblTue", "Tue"), value: 2 },
  { label: t("lblWed", "Wed"), value: 3 },
  { label: t("lblThu", "Thu"), value: 4 },
  { label: t("lblFri", "Fri"), value: 5 },
  { label: t("lblSat", "Sat"), value: 6 },
  { label: t("lblSun", "Sun"), value: 0 },
];

const CronjobForm = observer(({ controller }: { controller: CronjobController }) => {
  const [form] = Form.useForm();
  controller.setFormRef(form);

  const { cronjob, cronjobTypes, isReadOnly, formMode, formFieldsSize } = controller;

  if (!cronjob) return null;

  const setField = (key: string, value: any) => {
    controller.setCronjob(value, key);
  };

  const setSchedule = (key: string, value: any) => {
    const next = { ...cronjob.schedule, [key]: value };
    controller.setCronjob(next, "schedule");
  };

  const timeValue = cronjob.schedule?.time ? dayjs(cronjob.schedule.time, "HH:mm") : null;

  return (
    <Form
      form={form}
      className={styles.form}
      requiredMark={false}
      initialValues={{
        name: cronjob.name,
        type: cronjob.type,
        frequency: cronjob.schedule?.frequency,
        time: timeValue,
        timezone: cronjob.timezone,
        daysOfWeek: cronjob.schedule?.daysOfWeek || [],
        payloadJson: cronjob.payloadJson || "{}",
      }}
      layout="vertical"
    >
      {formMode === "edit" && (
        <div
          className={styles.newRow}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "8px 12px",
            background: "#fafafa",
            border: "1px solid #f0f0f0",
            borderRadius: 4,
            marginBottom: 12,
          }}
        >
          <span style={{ fontSize: 12, color: "#666" }}>{t("lblLastRun", "Last run")}:</span>
          {cronjob.lastRunAt ? (
            <>
              <strong style={{ fontSize: 12 }}>
                {dayjs(cronjob.lastRunAt).format("DD/MM/YYYY HH:mm")}
              </strong>
              {cronjob.lastRunStatus && (
                <Tag
                  color={cronjob.lastRunStatus === "success" ? "green" : "red"}
                  style={{ margin: 0, lineHeight: "18px" }}
                >
                  {cronjob.lastRunStatus}
                </Tag>
              )}
              {cronjob.lastRunError && (
                <Tooltip title={cronjob.lastRunError}>
                  <span style={{ fontSize: 12, color: "#cf1322", cursor: "help" }}>(error)</span>
                </Tooltip>
              )}
            </>
          ) : (
            <em style={{ fontSize: 12, color: "#999" }}>{t("lblNever", "never")}</em>
          )}
          <div style={{ flex: 1 }} />
          <Popconfirm
            title={t(
              "lblConfirmResetLastRun",
              "Clear last-run timestamp so the worker re-runs this cronjob on the next tick?"
            )}
            onConfirm={() => controller.resetLastRun(cronjob._id)}
            okText={t("lblYes", "Yes")}
            cancelText={t("lblNo", "No")}
            disabled={isReadOnly || !cronjob.lastRunAt}
          >
            <Button
              size="small"
              icon={<FontAwesomeIcon icon={faRotateLeft} />}
              disabled={isReadOnly || !cronjob.lastRunAt}
            >
              {t("lblResetLastRun", "Reset Last Run")}
            </Button>
          </Popconfirm>
        </div>
      )}

      <Row className={styles.newRow} gutter={20}>
        <Col span={12}>
          <Form.Item
            colon={false}
            label={
              <Badge dot={!cronjob.name}>
                <span className={styles.formTitle}>{t("lblName", "Name")}</span>
              </Badge>
            }
            name="name"
            rules={[{ required: true, whitespace: true, message: t("lblNameIsRequired", "Name is required") }]}
          >
            <Input
              name="name"
              onChange={(e) => setField("name", e.target.value)}
              disabled={isReadOnly}
              autoFocus={formMode === "create"}
              maxLength={120}
              size={formFieldsSize}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            colon={false}
            label={
              <Badge dot={!cronjob.type}>
                <span className={styles.formTitle}>{t("lblType", "Type")}</span>
              </Badge>
            }
            name="type"
            rules={[{ required: true, message: t("lblTypeIsRequired", "Type is required") }]}
          >
            <Select
              showSearch
              optionFilterProp="children"
              disabled={isReadOnly}
              value={cronjob.type}
              onChange={(value) => setField("type", value)}
              placeholder={t("lblSelectAType", "Select a type")}
              size={formFieldsSize}
            >
              {(cronjobTypes || []).map((ct: any) => (
                <Select.Option key={ct._id} value={ct._id}>
                  {ct.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>

      <Row className={styles.newRow} gutter={20}>
        <Col span={12}>
          <Form.Item
            colon={false}
            label={<span className={styles.formTitle}>{t("lblFrequency", "Frequency")}</span>}
            name="frequency"
            rules={[{ required: true, message: t("lblFrequencyIsRequired", "Frequency is required") }]}
          >
            <Radio.Group
              disabled={isReadOnly}
              onChange={(e) => setSchedule("frequency", e.target.value)}
              value={cronjob.schedule?.frequency}
            >
              <Radio.Button value="daily">{t("lblDaily", "Daily")}</Radio.Button>
              <Radio.Button value="weekly">{t("lblWeekly", "Weekly")}</Radio.Button>
            </Radio.Group>
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item
            colon={false}
            label={<span className={styles.formTitle}>{t("lblTime", "Time")}</span>}
            name="time"
            rules={[{ required: true, message: t("lblTimeIsRequired", "Time is required") }]}
          >
            <TimePicker
              value={timeValue}
              format="HH:mm"
              minuteStep={1}
              disabled={isReadOnly}
              allowClear={false}
              onChange={(value) => setSchedule("time", value ? value.format("HH:mm") : "")}
              style={{ width: "100%" }}
              size={formFieldsSize}
            />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item
            colon={false}
            label={<span className={styles.formTitle}>{t("lblTimezone", "Timezone")}</span>}
            name="timezone"
          >
            <Select
              value={cronjob.timezone || "UTC"}
              options={TIMEZONES}
              disabled={isReadOnly}
              onChange={(value) => setField("timezone", value)}
              size={formFieldsSize}
            />
          </Form.Item>
        </Col>
      </Row>

      {cronjob.schedule?.frequency === "weekly" && (
        <Row className={styles.newRow} gutter={20}>
          <Col span={24}>
            <Form.Item
              colon={false}
              label={<span className={styles.formTitle}>{t("lblDaysOfWeek", "Days of Week")}</span>}
              name="daysOfWeek"
              rules={[
                {
                  validator: (_rule, value) =>
                    Array.isArray(value) && value.length > 0
                      ? Promise.resolve()
                      : Promise.reject(new Error(t("lblPickAtLeastOneDay", "Pick at least one day"))),
                },
              ]}
            >
              <Checkbox.Group
                options={DAY_OPTIONS}
                value={cronjob.schedule?.daysOfWeek || []}
                disabled={isReadOnly}
                onChange={(value) => setSchedule("daysOfWeek", value)}
              />
            </Form.Item>
          </Col>
        </Row>
      )}

      <Row className={styles.newRow} gutter={20}>
        <Col span={24}>
          <Form.Item
            colon={false}
            label={
              <Badge dot={!cronjob.payloadJson || cronjob.payloadJson.trim() === ""}>
                <span className={styles.formTitle}>{t("lblPayloadJSON", "Payload (JSON)")}</span>
              </Badge>
            }
            name="payloadJson"
            rules={[
              {
                validator: (_rule, value) => {
                  if (!value || !value.trim()) return Promise.reject(new Error(t("lblPayloadIsRequired", "Payload is required")));
                  try {
                    const parsed = JSON.parse(value);
                    if (parsed === null || typeof parsed !== "object") {
                      return Promise.reject(new Error(t("lblPayloadMustBeAJSONObjectOrArray", "Payload must be a JSON object or array")));
                    }
                    return Promise.resolve();
                  } catch {
                    return Promise.reject(new Error(t("lblPayloadIsNotValidJSON", "Payload is not valid JSON")));
                  }
                },
              },
            ]}
          >
            <TextArea
              autoSize={{ minRows: 8, maxRows: 20 }}
              value={cronjob.payloadJson}
              disabled={isReadOnly}
              onChange={(e) => setField("payloadJson", e.target.value)}
              style={{ fontFamily: "monospace" }}
            />
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
});

export { CronjobForm };
